import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '工口游戏攻略大全是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '工口游戏攻略大全是专注于中文工口游戏资讯的综合性平台，提供游戏推荐、类型介绍、玩法攻略、新作速递及平台下载指引，帮助玩家快速找到心仪的工口游戏。',
        },
      },
      {
        '@type': 'Question',
        name: '网站上的游戏如何获取？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们提供各大下载平台的详细指引，包括DLsite、Steam、FANZA GAMES等主流平台的对比评测与使用教程。请注意，下载前务必确认年龄限制与当地法律法规。',
        },
      },
      {
        '@type': 'Question',
        name: '网站多久更新一次内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们持续关注工口游戏行业动态，每周至少更新3-5篇新内容，包括新作评测、攻略指南与行业资讯，确保玩家获取最新最全的信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系我们？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页的联系表单与我们取得联系，也可发送邮件至我们的官方邮箱。我们会在1-2个工作日内回复您的疑问或建议。',
        },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '工口游戏攻略大全',
    url: 'https://eroge-guide.com/',
    description: '专注中文工口游戏资讯的综合性平台，提供游戏推荐、攻略与下载指引。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qq5cfnahb7382ovdeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 工口游戏攻略大全"
        description="了解工口游戏攻略大全的品牌故事、核心团队与口碑评价。我们致力于为中文玩家提供最全面的工口游戏资讯与攻略服务。"
        keywords="工口游戏,关于我们,工口游戏攻略,游戏资讯平台,联系我们"
        canonicalUrl="https://eroge-guide.com/about"
        ogType="website"
        schemaJson={faqSchema}
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                工口游戏攻略大全，致力于为中文玩家打造最全面、专业的工口游戏资讯平台。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  成为中文圈最受信赖的工口游戏资讯平台，让每一位玩家都能在这里找到属于自己的游戏归宿。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-team-line text-2xl"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">核心团队</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  由资深工口游戏玩家组成的编辑团队，拥有十年以上游戏阅历，深度评测每一款作品。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">内容准则</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  坚持客观公正的评测标准，所有推荐内容均经过实际体验，拒绝软文与虚假宣传。
                </p>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="mb-16 py-10 px-6 md:px-10 bg-background-100 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              品牌故事
            </h2>
            <div className="max-w-3xl mx-auto space-y-4 text-foreground-700 leading-relaxed">
              <p>
                工口游戏攻略大全诞生于一群工口游戏爱好者的共同心愿——让更多中文玩家能够轻松发现优秀的工口游戏作品。
              </p>
              <p>
                我们深知，面对海量的工口游戏，玩家往往难以快速判断哪些作品值得投入时间。因此，我们组建了一支由资深玩家、游戏编辑与攻略写手组成的专业团队，致力于为每一款工口游戏提供详尽、客观的评测与攻略。
              </p>
              <p>
                从立项至今，我们已经累计评测超过200款工口游戏，覆盖RPG、AVG、SLG、ACT等全部主流品类。我们的攻略文章被各大游戏社区广泛引用，月度活跃读者突破10万人次。
              </p>
              <p>
                未来，我们将继续深耕工口游戏领域，拓展更多品类覆盖，优化阅读体验，并计划推出视频攻略与社区互动功能，让工口游戏玩家拥有一个真正属于他们的交流平台。
              </p>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              玩家口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  &ldquo;攻略写得非常详细，帮我通关了好几款卡关的RPG。分类也很清晰，找游戏特别方便。&rdquo;
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— RPG爱好者 小明</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  &ldquo;排行榜很靠谱，跟着推荐的游戏玩基本没有踩过雷。资讯更新也很及时！&rdquo;
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— AVG控 雪月花</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  &ldquo;平台下载指引那篇文章太实用了，终于搞清楚了各个平台的区别。希望以后多出这类实用内容。&rdquo;
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— SLG新手 铁拳无敌</p>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-2xl mx-auto p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60">
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的称呼
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="suggestion">内容建议</option>
                    <option value="cooperation">商务合作</option>
                    <option value="feedback">问题反馈</option>
                    <option value="other">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact_message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                {/* Honeypot */}
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">
                    感谢您的留言，我们会尽快回复！
                  </p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-primary-600 text-center">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqSchema.mainEntity.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-50 border border-background-200/60 rounded-lg cursor-pointer"
                >
                  <summary className="px-5 py-4 text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                    {faq.name}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />

      {/* Organization Schema - separate script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
    </>
  );
}